import React, {useContext} from "react";
import {View, Text} from '@react-pdf/renderer';
import PropTypes from "prop-types";

import {
  getCurrencySign,
  getPriceWithCurrency,
  getQuotePriceWithTaxes,
  getTotalPrice, sortByOrder,
  toFixedPrice
} from "../../../misc/helpers";
import {DISCOUNT_TYPES} from "../../priceWorksheet/discounstInterface";
import {
  getDepositAmount, getDepositDiscount,
  getDiscountsForSection,
  getSubTotalPrice,
  shouldDisplayDiscount
} from "../../priceWorksheet/discountFunctions";
import {PrintDocumentContext} from "./printDocumentContext";
import PrintDocumentDisclaimer from "./printDocumentDisclaimer";
import {getShowPrice} from "../../../misc/priceFunctions";
import {getDifferentCurrencyCartItems} from "../../../misc/misc2";
import {isCafeCountetops} from "../../../misc/fabricatorHardcode";

const PrintDocumentTotal = (props) => {
  const {styles, priceWorksheetState, fabricatorData, locale, cartItems} = props;
  const {discountSections, customAddedDiscounts} = priceWorksheetState;
  const fabricatorSettings = fabricatorData.settings;
  const printContext = useContext(PrintDocumentContext);

  if (fabricatorData.isAmericanImport) {
    return null
  }

  let subTotalPrice = getTotalPrice(null, false).materialPrice;
  const totalPrice = getTotalPrice();
  let grandTotal = getQuotePriceWithTaxes(totalPrice);

  let allTaxes = totalPrice.priceTaxes.length
    ? totalPrice.priceTaxes
    : [{name: "", materialTax: totalPrice.materialTaxes}];
  const showTaxes = allTaxes.some(tax => tax.materialTax > 0);

  const roundedPrice = (price) => toFixedPrice(Math.trunc(price * 100) / 100);

  const getDiscountPercentageAmount = (discountSections, discountSection, discount) => {
    const currentTotal = getSubTotalPrice(discountSections, discountSection, discount, true, customAddedDiscounts);
    const priceTaken = currentTotal * discount.amount / 100 // for ex. 25% from 2700 = 625
    return roundedPrice(priceTaken);
  };

  let discountsTotalAmount = 0;
  if (discountSections.length) {
    discountSections.forEach(discountSection => {
      const allDiscounts = sortByOrder(getDiscountsForSection(discountSection, customAddedDiscounts));
      allDiscounts.forEach(discount => {
        if (shouldDisplayDiscount(discount)) {
          const discountAmount = (discount.type === DISCOUNT_TYPES.price)
            ? discount.amount
            : getDiscountPercentageAmount(discountSections, discountSection, discount);
          discountsTotalAmount = discountsTotalAmount + (+discountAmount);
        }
      })
    })
  }

  const showPrice = getShowPrice(fabricatorData, true);

  if (!showPrice) {
    return (
      <View style={styles.footer} wrap={false}>
        <PrintDocumentDisclaimer withTotal={true}
                                 styles={styles}
                                 locale={locale}
                                 fabricatorData={fabricatorData}/>
      </View>
    )
  }

  const differentCurrencyObjects = getDifferentCurrencyCartItems(fabricatorSettings, cartItems);

  return (
    <View style={styles.footer} wrap={false}>
      <View style={styles.footerLeft}>
        <PrintDocumentDisclaimer withTotal={true}
                                 styles={styles}
                                 locale={locale}
                                 fabricatorData={fabricatorData}/>
      </View>
      <View style={styles.total}>
        {differentCurrencyObjects.length ? differentCurrencyObjects.map((currObject, currObjectIndex) => {
          let currencySubTotal = getTotalPrice(null, false, currObject.cartItems).materialPrice;
          currencySubTotal = toFixedPrice(currencySubTotal, true);
          return (
            <View style={currObjectIndex ? styles.totalSection : styles.totalSectionFirst} key={currObject.currency}>
              <Text style={styles.totalSectionText}>{printContext.messages["receipt.total.subTotal"]}:</Text>
              <Text style={styles.totalSectionAmount}>
                {getCurrencySign(currObject.currency, fabricatorSettings) + currencySubTotal}
              </Text>
            </View>
          )
        }) : null}
        <View style={differentCurrencyObjects.length ? styles.totalSection : styles.totalSectionFirst}>
          <Text style={styles.totalSectionText}>{printContext.messages["receipt.total.subTotal"]}:</Text>
          <Text style={styles.totalSectionAmount}>{getPriceWithCurrency(fabricatorSettings, subTotalPrice, null, false, true)}</Text>
        </View>
        {discountsTotalAmount ? (
          <View style={styles.totalSection}>
            <Text style={styles.totalSectionText}>{printContext.messages["receipt.total.discounts"]}:</Text>
            <Text style={styles.totalSectionAmount}>-{getPriceWithCurrency(fabricatorSettings, roundedPrice(discountsTotalAmount), null, false, true)}</Text>
          </View>
        ) : null}
        {showTaxes ? allTaxes.map((taxObject, taxObjectIndex) => {
          const taxTotal = taxObject.materialTax;
          const taxName = taxObject.nameIntl && taxObject.nameIntl[locale]
            ? taxObject.nameIntl[locale]
            : taxObject.state || printContext.messages["receipt.total.tax"]
          return (
            <View style={styles.totalSection} key={taxObjectIndex}>
              <Text style={styles.totalSectionText}>{taxName}</Text>
              <Text style={styles.totalSectionAmount}>{getPriceWithCurrency(fabricatorSettings, taxTotal, null, false, true)}</Text>
            </View>
          )
        }) : null}
        {totalPrice.installationTaxes ? (
          <View style={styles.totalSection}>
            <Text style={styles.totalSectionText}>{printContext.messages["receipt.total.installationTaxes"]}</Text>
            <Text style={styles.totalSectionAmount}>{getPriceWithCurrency(fabricatorSettings, totalPrice.installationTaxes, null, false, true)}</Text>
          </View>
        ) : null}
        {fabricatorSettings.useShipping || isCafeCountetops ? (
          <View style={styles.totalSection}>
            <Text style={styles.totalSectionText}>{printContext.messages["grohe.cart.confirm.columns.delivery"]}</Text>
            <Text style={styles.totalSectionAmount}>{getPriceWithCurrency(fabricatorSettings, totalPrice.totalDeliveryCost, null, false, true)}</Text>
          </View>
        ) : null}
        {(fabricatorSettings.deposit && fabricatorSettings.enableDeposit) || getDepositDiscount(discountSections) ? (
          <View style={styles.totalSection}>
            <Text style={styles.totalSectionText}>{printContext.messages["depositPopup.depositAmount"]}:</Text>
            <Text style={styles.totalSectionAmount}>
              {getPriceWithCurrency(fabricatorSettings, getDepositAmount(discountSections, grandTotal, fabricatorData), null, false, true)}
            </Text>
          </View>
        ) : null}
        {differentCurrencyObjects.length ? differentCurrencyObjects.map(currObject => {
          const currentTotal = getTotalPrice(null, true, currObject.cartItems);
          const total = toFixedPrice(getQuotePriceWithTaxes(currentTotal), true)
          return (
            <View style={styles.totalSection} key={currObject.currency}>
              <Text style={styles.totalSectionText}>{printContext.messages["receipt.total.total"]}:</Text>
              <Text style={styles.totalSectionAmountFinal}>{getCurrencySign(currObject.currency, fabricatorSettings) + total}</Text>
            </View>
          )
        }) : null}
        <View style={styles.totalSection}>
          <Text style={styles.totalSectionText}>{printContext.messages["receipt.total.total"]}:</Text>
          <Text style={styles.totalSectionAmountFinal}>{getPriceWithCurrency(fabricatorSettings, grandTotal, null, false, true)}</Text>
        </View>
      </View>
    </View>
  )
};

PrintDocumentTotal.propTypes = {
  priceWorksheetState: PropTypes.object.isRequired,
  fabricatorData: PropTypes.object.isRequired,
  styles: PropTypes.object.isRequired,
  cartItems: PropTypes.array.isRequired,
};

export default PrintDocumentTotal
